import classNames from 'classnames'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Checkbox } from 'core-components/library/checkbox'
import { Flex } from 'core-components/library/flex'
import { Loader } from 'core-components/library/loader'
import { Actions } from 'core-components/library/modalDialog'
import { ToggleButtons } from 'core-components/library/toggle'
import { FlexGridWrapperContext } from 'core-components/sitebuilder/layout/context/FlexGridWrapperContext'
import { getLayoutProperties } from 'core-components/sitebuilder/layout/flexGridWrapper/settingsButton/helpers/getLayoutProperties'
import { useCurrentSitePage } from 'core-components/sitebuilder/layout/hooks/useCurrentSitePage'
import { useLayoutConfig } from 'core-components/sitebuilder/layout/hooks/useLayoutConfig'
import {
  ILayoutSettings,
  mapLayoutSettingProperty,
} from 'core-components/sitebuilder/models/ILayoutSettings'
import { ISitePage } from 'core-components/sitebuilder/models/ISitePage'
import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'
import { selectSitePagesSettings } from 'core-components/sitebuilder/store/selectors/selectSitePagesSettings'

import styles from './LayoutModal.module.scss'

export const LayoutModal = () => {
  const currentSitePage = useCurrentSitePage()
  const appId = useSelector(selectAppId)
  const sitePagesSettings = useSelector(selectSitePagesSettings)
  const { canEditSettings, updateUserLayout, saveAsDefaultLayout } =
    useContext(FlexGridWrapperContext)

  const [editSitePage, setEditSitePage] = useState<ISitePage>({ ...currentSitePage })
  const [isSavingDefault, setIsSavingDefault] = useState<boolean>(false)
  const selectedLayoutSettings = useLayoutConfig({ id: editSitePage?.layoutUser })
  const isResetDisabled =
    editSitePage.layout === editSitePage.layoutUser &&
    JSON.stringify(currentSitePage.layoutSettings) ===
      JSON.stringify(editSitePage.layoutUserSettings)
  const isSaveDisabled =
    currentSitePage.layoutUser === editSitePage.layoutUser &&
    JSON.stringify(currentSitePage.layoutUserSettings) ===
      JSON.stringify(editSitePage.layoutUserSettings)
  const settingsProperties = Object.keys(selectedLayoutSettings?.properties || {})

  const layoutItems = useMemo(() => {
    return sitePagesSettings.map((layout) => ({
      key: layout.name,
      text: layout.name,
    }))
  }, [])

  const onLayoutChange = useCallback(
    (layout: string, properties: ILayoutSettings['properties']) => {
      const isCurrentPageLayout = editSitePage.layout === layout

      const newEditSitePage: ISitePage = {
        ...editSitePage,
        layoutUser: layout,
        layoutUserSettings: isCurrentPageLayout
          ? editSitePage.layoutSettings
          : getLayoutProperties(properties),
      }
      setEditSitePage(newEditSitePage)
    },
    [editSitePage]
  )

  const saveAsUserLayout = (page: ISitePage) => {
    updateUserLayout(page.layoutUser, page.layoutUserSettings)
  }

  const saveAsDefaultLayoutHandler = useCallback(async () => {
    const newCurrentSitePage = {
      ...currentSitePage,
      layout: editSitePage.layoutUser,
      layoutSettings: editSitePage.layoutUserSettings,
      layoutUserSettings: editSitePage.layoutUserSettings,
    }
    setIsSavingDefault(true)

    await saveAsDefaultLayout(newCurrentSitePage, editSitePage.widgetsData)

    setEditSitePage(newCurrentSitePage)
    setIsSavingDefault(false)
  }, [currentSitePage, editSitePage, appId])

  const onResetAll = useCallback(() => {
    const widgetsData = [...currentSitePage.availableWidgetsData.map((i) => ({ ...i }))]
    const layoutUser = currentSitePage.layout
    const layoutUserSettings = currentSitePage.layoutSettings
    const newEditSitePage: ISitePage = {
      ...editSitePage,
      widgetsData,
      layoutUser,
      layoutUserSettings,
    }
    setEditSitePage(newEditSitePage)
  }, [currentSitePage, editSitePage])

  return (
    <div className={styles.root}>
      <div className={styles.rootWrapper}>
        <div className={styles.header}>
          <i className="kbase kbase-settings" />
          <div className={styles.title}>Page Layout</div>
        </div>
        <div className={styles.content}>
          <ToggleButtons
            items={layoutItems}
            value={editSitePage.layoutUser}
            onChange={(value) => {
              const settingsItem = sitePagesSettings.find((el) => el.name === value)
              onLayoutChange(value, settingsItem?.properties || {})
            }}
          />
        </div>

        {settingsProperties.length ? (
          <>
            <div className={classNames(styles.header, styles.settingsHeader)}>
              <i className="kbase kbase-settings" />
              <div className={styles.title}>Layout Settings</div>
            </div>

            <Flex flexDirection={'column'} className={styles.content}>
              {settingsProperties.map((property: string) => {
                const settings = editSitePage.layoutUserSettings || {}
                const propertyData = mapLayoutSettingProperty(
                  selectedLayoutSettings?.properties[property]
                )
                return (
                  <Flex key={property}>
                    <Checkbox
                      key={property}
                      checked={settings[property]}
                      onChange={(newValue: boolean) => {
                        const newEditSitePage: ISitePage = {
                          ...editSitePage,
                          layoutUserSettings: {
                            ...editSitePage.layoutUserSettings,
                            [property]: newValue,
                          },
                        }
                        setEditSitePage(newEditSitePage)
                      }}
                      label={propertyData?.description || property}
                    />
                  </Flex>
                )
              })}
            </Flex>
          </>
        ) : null}
      </div>

      <Actions
        position={'end'}
        items={[
          {
            onClick: saveAsDefaultLayoutHandler,
            id: 'saveAsDefault',
            label: isSavingDefault ? (
              <Loader dimension={{ width: 20, height: 20 }} />
            ) : (
              'Save as Default'
            ),
            variant: 'contained',
            disabled: !canEditSettings || isSavingDefault,
          },
          {
            id: 'reset',
            label: 'Reset to Default',
            disabled: isResetDisabled,
            variant: 'contained',
            onClick: onResetAll,
          },
          {
            id: 'save',
            label: 'Save',
            disabled: isSaveDisabled,
            variant: 'contained',
            onClick: () => saveAsUserLayout(editSitePage),
          },
        ]}
      />
    </div>
  )
}
